/* Audit Assistant — chat state machine (frontend-only).
 *
 * Owns messages[], typing, input, conversationId and the send /
 * regenerate / clear / newChat actions. Talks to a ChatProvider (mock
 * by default), so swapping in a real backend needs no change here or in
 * the UI — just a different provider instance.
 */

import { useCallback, useRef, useState } from 'react';
import type { ChatProvider } from '../mock/chatProvider';
import { defaultChatProvider } from '../mock/chatProvider';
import { DEFAULT_PROJECT } from '../mock/mockResponses';

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  ts: number; // epoch ms (stamped at creation)
  /** Assistant only — suggested next questions for this answer. */
  followUps?: string[];
}

let _seq = 0;
function nextId(): string {
  _seq += 1;
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore — fall through to counter id */
  }
  return `m_${_seq}`;
}

function newConversationId(): string {
  return `conv_${nextId()}`;
}

export interface UseMockChat {
  messages: ChatMessage[];
  typing: boolean;
  input: string;
  conversationId: string;
  project: string;
  setProject: (p: string) => void;
  setInput: (v: string) => void;
  send: (text?: string) => void;
  regenerate: (assistantId: string) => void;
  clear: () => void;
  newChat: () => void;
}

export function useMockChat(provider: ChatProvider = defaultChatProvider): UseMockChat {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string>(() => newConversationId());
  const [project, setProjectState] = useState<string>(DEFAULT_PROJECT);
  // Guards against overlapping sends resolving out of order.
  const pendingRef = useRef(0);
  // Latest project, readable inside the async provider callback.
  const projectRef = useRef(project);

  const setProject = useCallback((p: string) => {
    projectRef.current = p;
    setProjectState(p);
  }, []);

  const runAssistant = useCallback(
    (convId: string, prompt: string) => {
      const token = ++pendingRef.current;
      setTyping(true);
      void provider.sendMessage(convId, prompt).then((res) => {
        // Ignore if a newer send / clear superseded this one.
        if (token !== pendingRef.current) return;
        // Substitute the selected project into the mock answer.
        const text = res.text.replace(/\{project\}/g, projectRef.current);
        setTyping(false);
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'assistant', text, ts: Date.now(), followUps: res.followUps },
        ]);
      });
    },
    [provider],
  );

  const send = useCallback(
    (text?: string) => {
      const body = (text ?? input).trim();
      if (!body || typing) return;
      const userMsg: ChatMessage = { id: nextId(), role: 'user', text: body, ts: Date.now() };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      runAssistant(conversationId, body);
    },
    [input, typing, conversationId, runAssistant],
  );

  const regenerate = useCallback(
    (assistantId: string) => {
      if (typing) return;
      // Find the user prompt immediately preceding this assistant message.
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === assistantId);
        if (idx <= 0) return prev;
        const priorUser = [...prev.slice(0, idx)].reverse().find((m) => m.role === 'user');
        if (!priorUser) return prev;
        // Drop the old assistant message; a fresh one is appended by runAssistant.
        const trimmed = prev.filter((m) => m.id !== assistantId);
        // Defer the provider call until after state settles.
        queueMicrotask(() => runAssistant(conversationId, priorUser.text));
        return trimmed;
      });
    },
    [typing, conversationId, runAssistant],
  );

  const clear = useCallback(() => {
    pendingRef.current++; // invalidate any in-flight response
    setTyping(false);
    setMessages([]);
    setInput('');
  }, []);

  const newChat = useCallback(() => {
    pendingRef.current++;
    setTyping(false);
    setMessages([]);
    setInput('');
    setConversationId(newConversationId());
  }, []);

  return {
    messages,
    typing,
    input,
    conversationId,
    project,
    setProject,
    setInput,
    send,
    regenerate,
    clear,
    newChat,
  };
}
